import DownloadIcon from '@mui/icons-material/DownloadRounded';
import OpenInNewIcon from '@mui/icons-material/OpenInNewRounded';
import { Button, Typography } from '@mui/material';

import { pdfFileName } from './pdfFileName';

interface PdfViewerBarProps {
  /** 1-based, as the reader counts them. */
  visiblePage: number;
  pageCount: number;
  /** The document's original URL, where it has one. The route out, never the
   *  download. Empty for a document reachable only through the API, whose
   *  bytes have no address a new tab could open. */
  directUrl: string;
  /** The parsed copy, as a local `blob:` URL. */
  downloadUrl: string;
  /** Names the download and the action that starts it. */
  title: string;
}

/**
 * Where the reader is in the document, and the two ways out of it.
 *
 * Wraps rather than compresses: at 360px the counter and both actions do not
 * share a line, and a squeezed row would take the buttons below the 44px
 * target before it ran out of width (§4).
 */
export const PdfViewerBar = ({
  visiblePage,
  pageCount,
  directUrl,
  downloadUrl,
  title,
}: PdfViewerBarProps) => (
  <footer className="flex shrink-0 flex-wrap items-center gap-x-2 gap-y-1 border-t border-divider bg-surface px-4 py-2">
    {/* A live count rather than a static one: on a phone the only indication of
        how much document is left is this line. */}
    <Typography variant="caption" className="text-content-muted" aria-live="polite">
      Page {visiblePage} of {pageCount}
    </Typography>

    {/* Both routes out, grouped to the right so the counter keeps the left
        edge. Neither is the route — the reader stays here to read — but one who
        wants the file itself should not go looking for the original link. */}
    <div className="ml-auto flex items-center gap-1">
      {/* Downloads the copy already parsed, not the source URL again: the blob
          is same-origin, so `download` is honoured and names the file — on a
          cross-origin `src` the browser ignores the attribute, and a signed URL
          may not answer twice at all. */}
      <Button
        component="a"
        href={downloadUrl}
        download={pdfFileName(directUrl, title)}
        size="small"
        endIcon={<DownloadIcon />}
        aria-label={`Download ${title}`}
      >
        Download
      </Button>

      {/* Kept even when the document rendered fine — some readers simply prefer
          their device's own viewer. Absent when the document has no address of
          its own: a dead link is worse than no link. */}
      {directUrl !== '' && (
        <Button
          component="a"
          href={directUrl}
          target="_blank"
          rel="noreferrer"
          size="small"
          endIcon={<OpenInNewIcon />}
        >
          Open in new tab
        </Button>
      )}
    </div>
  </footer>
);
